import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Text,
  Wrap,
} from "@chakra-ui/react";
import InputPhone from "@raiden/library/components/Form/InputPhone";
import ApiErrorMessage from "@raiden/library/components/ReactHookForm/ApiErrorMessage";
import FormControlRHF from "@raiden/library/components/ReactHookForm/FormController";
import ObserverRHF from "@raiden/library/components/ReactHookForm/ObserverRHF";
import {
  RENTAL_AREAS_BOOKINGS_CUSTOMER_TYPES,
  RENTAL_AREAS_BOOKINGS_CUSTOMER_TYPE_LIST,
  RENTAL_AREAS_BOOKINGS_CUSTOMER_TYPE_VALUE_PARTICULAR,
} from "@raiden/library/constants/rentalArea/booking";
import isDateUnavailable from "@raiden/library/helpers/datePicker/isDateUnavailable";
import { useApi } from "@raiden/library/hooks/useApi";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import capitalize from "@splitfire-agency/raiden-library/dist/libraries/utils/capitalize";
import dayjs from "dayjs";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/RentalArea").RentalArea} rentalArea
 */
/**
 * @param {Props} props
 */
export function FormCreateBookings({ rentalArea }) {
  const intl = useIntl();

  const translate = useTranslate();

  /** @type {import("react-hook-form").UseFormReturn<import("@raiden/library/types/rentalArea/Booking").RentalAreaBookingsSpecificFormValues>} */
  const { control, getValues } = useFormContext();

  const [date, setDate] = useState(getValues("data.start_at"));

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Country").Country[]>} */
  const { swrResponse: swrCountriesResponse } = useApi(
    generateApiUrl({ id: "@api.countries.viewAny", query: { per_page: 250 } }),
  );

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/rentalArea/Availability").RentalAreaAvailability[]>} */
  const { swrResponse: swrRentalAreaAvailabilityResponse } = useApi(
    generateApiUrl({
      id: "@api.rental_areas.availabilities.viewAny",
      query: {
        area_id: [rentalArea.id],
        min_date: dayjs(date)
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD"),
        max_date: dayjs(date)
          .add(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD"),
        include_booking_available_dates: true,
        max_duration: 3,
        per_page: 250,
      },
    }),
  );
  const availabilities = swrRentalAreaAvailabilityResponse.data?.data;

  function handleChangeDate(date) {
    setDate(date);
  }

  return (
    <Box flexGrow={1}>
      <ApiErrorMessage pb="1rem" withCaughtErrors={true} />

      <Text variant="h4" pb=".5rem">
        <FormattedMessage defaultMessage="Information de location" />
      </Text>

      <Flex
        direction={{ base: "column", md: "row" }}
        gap={{ base: ".5rem", md: "1rem" }}>
        <FormControl isRequired>
          <FormLabel>
            {intl.formatMessage({
              defaultMessage: "Date de début de location",
            })}
          </FormLabel>

          <HStack>
            <FormControlRHF
              control={control}
              type="datetime-picker"
              name="data.start_at"
              rules={{ required: true }}
              inputProps={{
                isDateUnavailable: (date) =>
                  isDateUnavailable({
                    date,
                    availabilities: availabilities?.[0].availability_dates,
                  }),
                shouldCloseOnDaySelect: false,
                enableKeyboardInput: true,
                shouldRenderTodayButton: false,
                minValue: dayjs().format("YYYY-MM-DD"),
                onChangeFocusDate: handleChangeDate,
              }}
            />

            <ObserverRHF
              names={["data.start_at"]}
              render={({ values: [startDate] }) => (
                <FormControlRHF
                  control={control}
                  name="data.start_at_hour"
                  rules={{ required: true, disabled: startDate === "" }}
                  renderWithFormControl={(field) => (
                    <Select {...field}>
                      {Object.keys(
                        availabilities?.[0].availability_dates?.[startDate] ??
                          {},
                      ).map((checkin) => (
                        <option key={checkin} value={checkin}>
                          {checkin.slice(0, 5)}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              )}
            />
          </HStack>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            {intl.formatMessage({
              defaultMessage: "Date de fin de location",
            })}
          </FormLabel>

          <HStack>
            <ObserverRHF
              names={["data.start_at", "data.start_at_hour"]}
              render={({ values: [startDate, checkin] }) => (
                <FormControlRHF
                  control={control}
                  type="datetime-picker"
                  name="data.end_at"
                  rules={{ required: true, disabled: checkin === "" }}
                  inputProps={{
                    isDateUnavailable: (date) =>
                      isDateUnavailable({
                        date,
                        startDate,
                        availabilities: availabilities?.[0].availability_dates,
                      }),
                    shouldCloseOnDaySelect: false,
                    enableKeyboardInput: true,
                    shouldRenderTodayButton: false,
                    minValue: startDate
                      ? dayjs(startDate).format("YYYY-MM-DD")
                      : dayjs().format("YYYY-MM-DD"),
                  }}
                />
              )}
            />

            <ObserverRHF
              names={["data.start_at", "data.start_at_hour", "data.end_at"]}
              render={({ values: [startDate, startHour, endDate] }) => (
                <FormControlRHF
                  control={control}
                  name="data.end_at_hour"
                  rules={{ required: true, disabled: endDate === "" }}
                  renderWithFormControl={(field) => (
                    <Select {...field}>
                      {(
                        availabilities?.[0].availability_dates?.[startDate]?.[
                          startHour
                        ]?.[endDate] ?? []
                      ).map((checkout) => (
                        <option key={checkout} value={checkout}>
                          {checkout.slice(0, 5)}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              )}
            />
          </HStack>
        </FormControl>

        <FormControlRHF
          control={control}
          name="data.capacity"
          type="number-input"
          rules={{ required: true }}
          minValue={1}
          maxValue={rentalArea.max_capacity}
          label={intl.formatMessage({
            defaultMessage: "Nombre de personnes",
          })}
        />
      </Flex>

      <Wrap mt={{ base: ".5rem", md: "1rem" }}>
        <FormControlRHF
          control={control}
          name="data.infos"
          type="textarea"
          withCharactersCounter={true}
          rules={{ required: true, maxLength: 2000 }}
          label={intl.formatMessage({
            defaultMessage: "Précisions sur votre projet de location",
          })}
        />
      </Wrap>

      <Text variant="h4">
        <FormattedMessage defaultMessage="Coordonnées personnelles" />
      </Text>

      <Flex
        mt={{ base: ".5rem", md: "1rem" }}
        direction={{ base: "column", md: "row" }}
        wrap="wrap"
        gap={{ base: ".5rem", md: "1rem" }}>
        <ObserverRHF
          names={["data.customer_type"]}
          render={({ values: [customerType] }) => {
            const isParticular =
              customerType ===
              RENTAL_AREAS_BOOKINGS_CUSTOMER_TYPE_VALUE_PARTICULAR;
            return (
              <>
                <Wrap flexGrow={1} minW={!isParticular ? "45%" : "25%"}>
                  <FormControlRHF
                    control={control}
                    name="data.customer_type"
                    rules={{ required: true }}
                    label={intl.formatMessage({
                      defaultMessage: "Type du client",
                    })}
                    renderWithFormControl={(field) => (
                      <Select
                        {...field}
                        placeholder={intl.formatMessage({
                          defaultMessage: "Choisir un type de client...",
                        })}>
                        {RENTAL_AREAS_BOOKINGS_CUSTOMER_TYPE_LIST.map(
                          (type) => (
                            <option key={type.id} value={type.id}>
                              {capitalize(
                                intl.formatMessage(
                                  RENTAL_AREAS_BOOKINGS_CUSTOMER_TYPES[type.id]
                                    .label,
                                ),
                              )}
                            </option>
                          ),
                        )}
                      </Select>
                    )}
                  />
                </Wrap>

                {!isParticular && (
                  <Wrap flexGrow={1} minW="45%">
                    <FormControlRHF
                      control={control}
                      name="data.company_name"
                      rules={
                        !isParticular ? { required: true } : { required: false }
                      }
                      label={intl.formatMessage({
                        defaultMessage: "Nom de la société",
                      })}
                    />
                  </Wrap>
                )}

                <Wrap flexGrow={1} minW={!isParticular ? "45%" : "25%"}>
                  <FormControlRHF
                    control={control}
                    name="data.first_name"
                    rules={{ required: true }}
                    label={intl.formatMessage({
                      defaultMessage: "Votre prénom",
                    })}
                  />
                </Wrap>

                <Wrap flexGrow={1} minW={!isParticular ? "45%" : "25%"}>
                  <FormControlRHF
                    control={control}
                    name="data.last_name"
                    rules={{ required: true }}
                    label={intl.formatMessage({
                      defaultMessage: "Votre nom",
                    })}
                  />
                </Wrap>
              </>
            );
          }}
        />
      </Flex>

      <Flex
        mt={{ base: ".5rem", md: "1rem" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: ".5rem", md: "1rem" }}>
        <FormControlRHF
          control={control}
          name="data.email"
          rules={{ required: true }}
          label={intl.formatMessage({
            defaultMessage: "Votre adresse email de contact",
          })}
        />

        <InputPhone
          countries={swrCountriesResponse.data?.data ?? []}
          nameCountry="data.phone_country"
          nameNumber="data.phone_number"
        />
      </Flex>

      <Flex
        mt="1rem"
        direction={{ base: "column", md: "row" }}
        gap={{ base: ".5rem", md: "1rem" }}>
        <FormControlRHF
          control={control}
          name="data.address1"
          rules={{ required: true }}
          label={intl.formatMessage({
            defaultMessage: "Votre adresse",
          })}
        />

        <FormControlRHF
          control={control}
          name="data.address2"
          label={intl.formatMessage({
            defaultMessage: "Complément d'adresse",
          })}
        />
      </Flex>

      <Flex
        mt={{ base: ".5rem", md: "1rem" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: ".5rem", md: "1rem" }}>
        <FormControlRHF
          control={control}
          name="data.zipcode"
          rules={{ required: true }}
          label={intl.formatMessage({
            defaultMessage: "Code postal",
          })}
        />

        <FormControlRHF
          control={control}
          name="data.city"
          rules={{ required: true }}
          label={intl.formatMessage({
            defaultMessage: "Ville",
          })}
        />

        <FormControlRHF
          control={control}
          name="data.country_id"
          rules={{ required: true }}
          label={intl.formatMessage({
            defaultMessage: "Pays",
          })}
          renderWithFormControl={(field) => (
            <Select
              {...field}
              placeholder={intl.formatMessage({
                defaultMessage: "Choisir un pays...",
              })}>
              {swrCountriesResponse.data?.data?.map((country) => (
                <option key={country.id} value={country.id}>
                  {translate(country.name)}
                </option>
              ))}
            </Select>
          )}
        />
      </Flex>
    </Box>
  );
}
